import * as React from "react";
import { Search, Info, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import ProjectCard from "../../../features/projects/components/molecules/ProjectCard";
import { WorkPillExecution } from "../../../types/navigationTypes";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface ProjectSearchListProps {
  projects: WorkPillExecution[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSelectProject: (executionId: string) => void;
  selectedExecutionId?: string;
  selectedWorkPill: string;
  onWorkPillChange: (workPill: string) => void;
  uniqueWorkPills: string[];
  sortBy: 'newest' | 'oldest';
  onSortByChange: (sort: 'newest' | 'oldest') => void;
}

export const ProjectSearchList: React.FC<ProjectSearchListProps> = ({
  projects,
  searchTerm,
  onSearchChange,
  onSelectProject,
  selectedExecutionId,
  selectedWorkPill,
  onWorkPillChange,
  uniqueWorkPills,
  sortBy,
  onSortByChange
}) => {
  // Convert uniqueWorkPills to SearchableSelectOption format
  const workPillOptions = React.useMemo(() => {
    const options = [
      { id: "all", name: "All WorkPills" }
    ];
    uniqueWorkPills.forEach(wp => {
      options.push({ id: wp, name: wp });
    });
    return options;
  }, [uniqueWorkPills]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border bg-slate-50/50 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search projects..."
            className="pl-9 bg-white"
          />
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1">
            <SearchableSelect
              options={workPillOptions}
              value={selectedWorkPill}
              onValueChange={onWorkPillChange}
              placeholder="Filter by WorkPill"
              searchPlaceholder="Search WorkPill..."
              emptyMessage="No WorkPills"
              className="h-8 text-xs bg-white"
            />
          </div>

          <div className="relative w-[140px]">
            <CalendarDays className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none z-10" />
            <Select value={sortBy} onValueChange={onSortByChange}>
              <SelectTrigger className="h-8 text-xs bg-white pl-8">
                <SelectValue>
                  {sortBy === 'newest' ? 'Newest' : 'Oldest'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 flex flex-col gap-2">
          {projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground text-center px-4">
              <Info className="w-8 h-8 opacity-50" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">No projects found</p>
                <p className="text-xs">
                  {searchTerm 
                    ? `No matches for "${searchTerm}"` 
                    : "Create a new project to get started"}
                </p>
              </div>
            </div>
          ) : (
            projects.map((project) => (
              <div 
                key={project.id}
                className={cn(
                  "cursor-pointer transition-all rounded-lg border border-transparent hover:border-border hover:bg-slate-50",
                  selectedExecutionId === project.id && "bg-gray-50 border-gray-100 hover:bg-gray-50 hover:border-gray-200"
                )}
                onClick={() => onSelectProject(project.id)}
              >
                <div className="pointer-events-none">
                  <ProjectCard 
                    execution={project} 
                    onClick={() => {}} 
                    compact={true} 
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
